import React, { useEffect, useState, useCallback, useRef } from 'react';
import './OrderSummary.css';
import { getApiUrl } from '../../config';

const OrderSummary = ({ total, shippingTotal, referenceCode, onRefCodeChange, cartItems, onDiscountUpdate, onKaporaInfoChange }) => {
  const [refCode, setRefCode] = useState(referenceCode || '');
  const [discount, setDiscount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [discountMessage, setDiscountMessage] = useState('');
  const [kaporaInfo, setKaporaInfo] = useState(null);

  const debounceTimer = useRef(null);
  const lastCheckedCode = useRef('');

  const calculateFinalTotal = () => {
    const subtotal = parseFloat(total);
    const shipping = parseFloat(shippingTotal);
    const rawTotal = subtotal + shipping - discount;
    const finalTotal = Math.max(0, rawTotal);
    return finalTotal.toLocaleString('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const handleRefCodeChange = (e) => {
    const newCode = e.target.value.toUpperCase();
    setRefCode(newCode);
    setError(null); // Clear previous errors on new input
    
    // Parent component'e ref code değişikliğini bildir
    if (onRefCodeChange) {
      onRefCodeChange(newCode);
    }
  };

  const checkRefCode = useCallback(async (codeToCheck) => {
    if (lastCheckedCode.current === codeToCheck && codeToCheck !== '') {
        return;
    }
    lastCheckedCode.current = codeToCheck;

    setIsLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/cart/discount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ref_code: codeToCheck,
          total_amount: total,
          cart_items: cartItems
        })
      });

      const data = await response.json();

      if (data.success) {
        setDiscount(data.indirim_tutari);
        setDiscountMessage(data.message);
        setError(null);
        if (onDiscountUpdate) onDiscountUpdate(data.indirim_tutari);
        // Kapora bilgisini ayarla ve parent component'e gönder
        if (data.kapora_info) {
          setKaporaInfo(data.kapora_info);
          if (onKaporaInfoChange) onKaporaInfoChange(data.kapora_info);
        } else {
          // Kapora bilgisi yoksa null gönder
          setKaporaInfo(null);
          if (onKaporaInfoChange) onKaporaInfoChange(null);
        }
      } else {
        setDiscount(0);
        setDiscountMessage('');
        setError(data.message);
        if (onDiscountUpdate) onDiscountUpdate(0);
        setKaporaInfo(null);
        if (onKaporaInfoChange) onKaporaInfoChange(null);
      }
    } catch (err) {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.');
      setDiscount(0);
      if (onDiscountUpdate) onDiscountUpdate(0);
      setKaporaInfo(null);
      if (onKaporaInfoChange) onKaporaInfoChange(null);
    } finally {
      setIsLoading(false);
    }
  }, [total, cartItems, onDiscountUpdate, onKaporaInfoChange]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (refCode.trim()) {
      debounceTimer.current = setTimeout(() => {
        checkRefCode(refCode.trim());
      }, 500); // 500ms gecikme
    } else {
      // Input boşsa indirimi ve mesajları sıfırla
      lastCheckedCode.current = '';
      setDiscount(0);
      setDiscountMessage('');
      setError(null);
      if (onDiscountUpdate) onDiscountUpdate(0);
      setKaporaInfo(null);
      if (onKaporaInfoChange) onKaporaInfoChange(null);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [refCode, checkRefCode, onDiscountUpdate, onKaporaInfoChange]);

  // Dışarıdan referenceCode prop'u değiştiğinde state'i güncelle
  useEffect(() => {
    if (referenceCode && referenceCode !== refCode) {
      setRefCode(referenceCode);
    }
  }, [referenceCode]);

  return (
    <div className='order-summary-first'>
      <div className="summary-item">
        <span>Ara Toplam</span>
        <span>{total} TL</span>
      </div>
      <div className="summary-item">
        <span>Kargo Ücreti</span>
        <span>{shippingTotal}TL</span>
      </div>

      <div className="reference-code-section">
        <p className="reference-title">Referans Kodu</p>
        <div className="reference-edit">
          <input
            type="text"
            value={refCode}
            onChange={handleRefCodeChange}
            placeholder="Referans kodunu girin"
            className="reference-input"
          />
        </div>
        {isLoading && <p className="loading">Kontrol ediliyor...</p>}
        {error && <p className="coupon-error">{error}</p>}
        {discountMessage && <p className="reference-message">{discountMessage}</p>}
      </div>

      {discount > 0 && (
        <div className="discount-section">
          <div className="summary-item discount">
            <span>İndirim</span>
            <span>-{discount.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} TL</span>
          </div>
          {discountMessage && <div className="discount-message">{discountMessage}</div>}
        </div>
      )}

      {kaporaInfo && kaporaInfo.success && (
        <div className="kapora-section">
          {kaporaInfo.allowed_products && kaporaInfo.allowed_products.length > 0 && (
            <div className="kapora-success">
              <div className="kapora-success-title">✅ Kapora Engeli Kaldırıldı</div>
              <div className="kapora-success-message">
                Ref kodunuz ile aşağıdaki ürünlerin kapora engeli kaldırıldı:
                <ul className="kapora-product-list">
                  {kaporaInfo.allowed_products.map((product, index) => (
                    <li key={index}>
                      <strong>{product.baslik}</strong> (Stok Kodu: {product.stok_kodu})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {kaporaInfo.kapora_products && kaporaInfo.kapora_products.length > 0 && (
            <div className="kapora-warning">
              <div className="kapora-warning-title">⚠️ Kapora Uyarısı</div>
              <div className="kapora-warning-message">
                Aşağıdaki ürünler kapora ile ayrılmış durumda. Ref kodunuz bu ürünler için geçerli değil:
                <ul className="kapora-product-list">
                  {kaporaInfo.kapora_products.map((product, index) => (
                    <li key={index}>
                      <strong>{product.baslik}</strong> (Stok Kodu: {product.stok_kodu})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="summary-item-total">
        <span>Toplam Tutar</span>
        <span>{calculateFinalTotal()} TL</span>
      </div>
    </div>
  );
};

export default OrderSummary;